import { action, computed, observable } from 'mobx';
import { BaseBlock } from '~/models/blocks/base-block.model';
import {
    IBillFromContent,
    IInvoiceHeaderBlockResponse,
    IInvoiceHeaderVariableValue,
    IInvoiceProjectDetails
} from '~/types/server.types';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import type { PaymentBlock } from '~/models/blocks/payment-block.model';
import { Variable } from '~/models/variable.model';
import { IImage, IInvoiceHeader } from '~/types/model.types';
import type { RecurringScheduleBlock } from '~/models/blocks/recurring-schedule-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { IInvoiceHeaderBlockStyles } from '~/types/block-styles.types';
import {
    formattedBillFrom,
    formattedProjectDetails
} from '~/components/Builder/Block/InvoiceBlock/InvoiceHeaderBlock.utils';

type InvoiceHeaderBlockDataForServer = ReturnType<
    BaseBlock['getDataForServer']
> &
    Pick<IInvoiceHeaderBlockResponse, 'invoice_header' | 'type'>;

export class InvoiceHeaderBlock extends BaseBlock {
    type: 'invoice_header_block';
    styles: IInvoiceHeaderBlockStyles;
    @observable invoice_header: IInvoiceHeader;

    constructor(
        block: IInvoiceHeaderBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.styles = block.styles;
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    get isDeletable() {
        return false;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get paymentScheduleBlock(): PaymentScheduleBlock {
        return (this.parent as InvoiceBlock).invoicePaymentScheduleBlock;
    }

    @computed
    get recurringScheduleBlock(): RecurringScheduleBlock {
        return (this.parent as InvoiceBlock).invoiceRecurringScheduleBlock;
    }

    @computed
    get isRecurringInvoice(): boolean {
        return (this.parent as InvoiceBlock).isRecurringInvoice;
    }

    @computed
    get nextPaymentBlock(): PaymentBlock {
        return this.paymentScheduleBlock?.nextPaymentBlock;
    }

    @computed
    get currentPaymentBlock() {
        return this.isRecurringInvoice
            ? this.recurringScheduleBlock?.currentPaymentBlock
            : this.nextPaymentBlock;
    }

    @computed
    get dateIssued() {
        return this.flow.first_published_at || new Date();
    }

    get connectedVariable(): Variable<IInvoiceHeaderVariableValue> {
        return super.connectedVariable;
    }

    @computed
    get invoiceNumber(): string {
        return this.connectedVariable?.value?.invoice_number;
    }

    @computed
    get poNumber(): string {
        return this.connectedVariable?.value?.po_number;
    }

    @computed
    get billToContent(): string {
        return this.connectedVariable?.value?.bill_to_content;
    }

    @computed
    get billToUser(): string {
        return this.connectedVariable?.value?.bill_to_user;
    }

    @computed
    get calculateCurrCompanyDetails(): IBillFromContent {
        const {
            companyStore: {
                currCompany: {
                    icon_image,
                    isDefaultCompanyIcon,
                    company_name,
                    email,
                    phone_number,
                    city,
                    state,
                    street_address,
                    zip_code,
                    country
                }
            }
        } = this.rootStore;

        const companyDetails = {
            company_icon_url: icon_image?.url,
            company_icon: icon_image,
            is_default_company_icon: isDefaultCompanyIcon,
            company_name: company_name,
            company_email: email,
            company_phone_number: phone_number,
            company_address: {
                city: city,
                state: state,
                street_address: street_address,
                zip_code: zip_code,
                country: country
            }
        };

        return companyDetails;
    }

    @computed
    get billFromContent(): IBillFromContent {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        return selectedFlow.isDirty || selectedFlow.is_template
            ? this.calculateCurrCompanyDetails
            : this.invoice_header?.bill_from_content;
    }
    @computed
    get formattedBillFrom(): string {
        return formattedBillFrom(this.billFromContent);
    }

    @computed
    get companyLogoUrl(): string {
        if (this.billFromContent?.company_icon_url) {
            return this.billFromContent.company_icon_url;
        }
        const {
            companyStore: {
                currCompany: { icon_image }
            }
        } = this.rootStore;
        return icon_image?.url;
    }

    @computed
    get companyLogo(): IImage {
        if (this.billFromContent?.company_icon) {
            return this.billFromContent.company_icon;
        }
        const {
            companyStore: {
                currCompany: { icon_image }
            }
        } = this.rootStore;
        return icon_image;
    }

    @computed
    get isDefaultHBLogo(): boolean {
        if (this.billFromContent?.is_default_company_icon) {
            return this.billFromContent.is_default_company_icon;
        }
        const {
            companyStore: {
                currCompany: { isDefaultCompanyIcon }
            }
        } = this.rootStore;
        return isDefaultCompanyIcon;
    }

    @computed
    get calculateCurrProjectDetails(): IInvoiceProjectDetails {
        const {
            builderUiStore: {
                selectedFlow: { project }
            }
        } = this.rootStore;

        const details = {
            project_name: project?.event_name,
            project_type: project?.event_type,
            project_date: project?.event_date,
            project_location: project?.event_location
        };
        return details;
    }

    @computed
    get projectDetails(): IInvoiceProjectDetails {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        if (selectedFlow.isDirty) {
            return this.calculateCurrProjectDetails;
        } else if (selectedFlow.is_template) {
            return null;
        } else {
            return this.invoice_header?.project_details;
        }
    }

    @computed
    get formattedProjectDetails(): string {
        return formattedProjectDetails(this.projectDetails);
    }

    getDataForServer(): InvoiceHeaderBlockDataForServer {
        return {
            ...super.getDataForServer(),
            invoice_header: this.invoice_header,
            type: this.type as 'invoice_header_block'
        };
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);

        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }
    }
}
